import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Check, Plus, X } from "lucide-react"
import { useEffect, useState } from "react"
import { toastApiError } from "../../../../api/apiError"
import { useUpdateConformityRecipients } from "../../../../api/conformityApi"

/** Chip-list editor for a conformity declaration's TO row.
 *
 *  Domain-adapted from the supplier-inquiry RecipientEditor but talks about
 *  the CUSTOMER (or a public catalogue for product-scope generic rows), not
 *  a supplier. The chip mechanics — inline "+ Add", per-chip remove, save-
 *  on-commit via useUpdateConformityRecipients — mirror the inquiry flow so
 *  the two Studio surfaces feel identical.
 *
 *  Primary chip:
 *    - Read-only when set (resolved server-side from mc_customer_contacts)
 *    - Rendered as a dashed placeholder when null (article/product scope,
 *      or a customer with no primary contact configured)
 *  Extras:
 *    - Per-declaration cc-list persisted on extra_recipient_emails
 *    - Each chip has an X to remove; the "+ Add" button expands inline
 *
 *  Every mutation replaces the whole extras list (PATCH /recipients with
 *  the full array), which keeps the endpoint dead simple and avoids diff
 *  races. Backend does NOT gate on row status — a downloaded / sent row
 *  can still gain a new cc (see mc_conformity_service.update_extra_recipients).
 */
export function ConformityRecipientEditor({
  declarationId,
  primary,
  extras,
  locked = false,
  primaryPlaceholder,
  onRemovePrimary,
  onPendingChange,
}: {
  declarationId: string
  primary: string | null
  extras: string[]
  /** Locked = post-audit read-only (currently unused — we allow edits at
   *  every status so the reviewer can re-send with new recipients). Kept
   *  as an opt-in prop so future flows can lock the list if legal wants. */
  locked?: boolean
  /** Text shown in the dashed placeholder when `primary` is null. Customer
   *  scope uses the default (RECIPIENT_PLACEHOLDER_NONE); product/article
   *  scope pass a placeholder that reads as "generic declaration, no
   *  specific recipient". */
  primaryPlaceholder?: string
  /** When given, the primary chip gets an X. The caller decides what removal
   *  means (the send modal drops the customer contact from THIS send only,
   *  the contact itself stays in master data). */
  onRemovePrimary?: () => void
  /** Reports the underlying mutation's pending state — the send modal uses
   *  it to keep Send disabled while a chip edit is still saving, closing the
   *  race between an in-flight recipient change and an in-flight send. */
  onPendingChange?: (pending: boolean) => void
}) {
  const { translate } = useTranslation()
  const update = useUpdateConformityRecipients()
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState("")

  useEffect(() => {
    onPendingChange?.(update.isPending)
  }, [update.isPending, onPendingChange])

  const placeholder =
    primaryPlaceholder ??
    translate("MC_CS_CONF_EDIT_RECIPIENT_PLACEHOLDER_NONE")

  async function persist(next: string[]) {
    try {
      await update.mutateAsync({ id: declarationId, emails: next })
    } catch (err: unknown) {
      toastApiError(err, translate, "MC_CS_CONF_EDIT_TOAST_RECIPIENTS_ERR")
    }
  }

  function commitAdd() {
    const trimmed = draft.trim()
    if (!trimmed) {
      setAdding(false)
      setDraft("")
      return
    }
    persist([...extras, trimmed])
    setAdding(false)
    setDraft("")
  }

  function removeAt(idx: number) {
    persist(extras.filter((_, i) => i !== idx))
  }

  return (
    <>
      {primary ? (
        <span
          className={
            onRemovePrimary && !locked
              ? "inline-flex h-6 items-center gap-1 rounded-md border border-grey4 bg-grey3 pl-2 pr-1 text-[11.5px] text-white"
              : "inline-flex h-6 items-center rounded-md border border-grey4 bg-grey3 px-2 text-[11.5px] text-white"
          }
        >
          {primary}
          {onRemovePrimary && !locked && (
            <button
              type="button"
              onClick={onRemovePrimary}
              className="rounded p-0.5 text-grey5 hover:bg-white/5 hover:text-white"
              aria-label={translate("MC_CS_CONF_EDIT_ARIA_REMOVE_RECIPIENT", {
                email: primary,
              })}
            >
              <X size={11} />
            </button>
          )}
        </span>
      ) : (
        <span className="inline-flex h-6 items-center rounded-md border border-dashed border-grey4 px-2 text-[11.5px] italic text-grey5">
          {placeholder}
        </span>
      )}

      {extras.map((email, i) => (
        <span
          key={`${email}-${i}`}
          className="inline-flex h-6 items-center gap-1 rounded-md border border-grey4 bg-grey3 pl-2 pr-1 text-[11.5px] text-white"
        >
          {email}
          {!locked && (
            <button
              type="button"
              onClick={() => removeAt(i)}
              disabled={update.isPending}
              className="rounded p-0.5 text-grey5 hover:bg-white/5 hover:text-white disabled:opacity-40"
              aria-label={translate("MC_CS_CONF_EDIT_ARIA_REMOVE_RECIPIENT", {
                email,
              })}
            >
              <X size={11} />
            </button>
          )}
        </span>
      ))}

      {locked ? null : adding ? (
        <span className="inline-flex h-6 items-center gap-1 rounded-md border border-grey4 bg-grey3 px-1.5 text-[11.5px]">
          <input
            autoFocus
            type="email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitAdd()
              if (e.key === "Escape") {
                setAdding(false)
                setDraft("")
              }
            }}
            placeholder={translate("MC_CS_CONF_EDIT_ADD_EMAIL_PLACEHOLDER")}
            className="w-[180px] bg-transparent text-[11.5px] text-white outline-hidden placeholder:text-grey5"
          />
          <button
            type="button"
            onClick={commitAdd}
            disabled={update.isPending}
            className="rounded p-0.5 text-success hover:bg-white/5 disabled:opacity-40"
            aria-label={translate("MC_CS_CONF_EDIT_ARIA_SAVE")}
          >
            <Check size={11} strokeWidth={3} />
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false)
              setDraft("")
            }}
            className="rounded p-0.5 text-grey5 hover:bg-white/5 hover:text-white"
            aria-label={translate("MC_CS_CONF_EDIT_ARIA_CANCEL")}
          >
            <X size={11} />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex h-6 items-center gap-1 rounded-md border border-dashed border-grey4 px-2 text-[11.5px] text-grey5 hover:border-white hover:text-white"
        >
          <Plus size={11} />
          {translate("MC_CS_CONF_EDIT_ADD_RECIPIENT")}
        </button>
      )}
    </>
  )
}
